<script setup>
import HeaderSwiper from '@/Components/Header/HeaderSwiper.vue';
import Icon from '@/Components/Images/Icon.vue';
import Navigation from '@/Components/Navigation/Navigation.vue';
import NavigationMobileIcon from '@/Components/Navigation/NavigationMobileIcon.vue';
import {useCart} from '@/stores/cart';
import {Link, usePage} from '@inertiajs/vue3';
import {breakpointsTailwind, useBreakpoints} from '@vueuse/core';
import {computed, inject, provide, ref, watch} from 'vue';
import CartIcon from "../../../img/cart.svg";
import logo from '../../../img/logo.svg';

const cart = useCart();

/** @type import('ziggy-js').default */
const route = inject('route');

const menuItems = computed(() => usePage().props.menus.find((header) => header.name === 'main'));
const openMobileMenu = ref(false);

const breakpoints = useBreakpoints(breakpointsTailwind);


const xlOrSmaller = breakpoints.smallerOrEqual('xl');


watch(xlOrSmaller, (value) => {
    if (!value && openMobileMenu.value) {
        openMobileMenu.value = false;
    }
});

provide('openMobileMenu', openMobileMenu);

</script>

<template>
    <header class="fixed left-0 top-0 z-40 w-full">
        <div class="bg-white bg-gradient-to-r from-primary to-secondary py-6 text-white shadow-sm">
            <div class="outer-container w-full">
                <div
                    class="mx-auto flex flex-col xl:flex-row xl:items-center xl:justify-between"
                >
                    <div class="flex flex-row items-center justify-between">
                        <div class="flex gap-4 xl:hidden">
                            <NavigationMobileIcon :open-mobile-menu="openMobileMenu" />
                        </div>
                        <Link
                            href="/"
                            class="shrink-0"
                        >
                            <img
                                class="h-[30px] w-35 md:w-[264px]"
                                :src="logo"
                                alt="Logo"
                            >
                        </Link>

                        <div class="flex-center gap-4 xl:hidden">
                            <Link
                                :href="route('public.pages.show', {
                                    page: usePage().props.commerceSettings?.shopping_cart_page_slug
                                })"
                                class="group flex items-center justify-between gap-2 hover:text-primary-200"
                            >
                                <div
                                    v-if="cart.cartCount"
                                    class="flex size-4 shrink items-center justify-center rounded-full bg-black p-3"
                                >
                                    {{ cart.cartCount }}
                                </div>
                                <div class="flex items-center justify-center">
                                    <Icon
                                        :icon="CartIcon"
                                        class="size-5 fill-primary group-hover:fill-primary"
                                    />
                                </div>
                            </Link>
                        </div>
                    </div>

                    <div
                        class="absolute left-0 top-[70px] z-10 w-screen gap-10 xl:relative xl:top-0 xl:flex xl:w-auto xl:items-center"
                    >
                        <div
                            :class="{ 'container lg:bg-unset z-20 h-screen w-full bg-gradient-to-r from-primary to-secondary pt-16 xl:h-auto xl:pt-0': openMobileMenu,}"
                        >
                            <div class="flex justify-center gap-8 text-center">
                                <Navigation
                                    v-if="menuItems"
                                    :menu-items="menuItems.contents"
                                />
                            </div>

                            <div
                                class="relative z-20 mt-8 flex flex-col items-start gap-3"
                                :class="{ hidden: !openMobileMenu }"
                            />
                        </div>
                    </div>


                    <div class="hidden items-center justify-center gap-8 xl:flex">
                        <Link
                            :href="route('public.pages.show', {
                                page: usePage().props.commerceSettings?.shopping_cart_page_slug
                            })"
                            class="group flex items-center justify-between gap-2 hover:text-primary-200"
                        >
                            <div
                                v-if="cart.cartCount"
                                class="flex size-4 shrink items-center justify-center rounded-full bg-black p-3"
                            >
                                {{ cart.cartCount }}
                            </div>
                            <div class="flex items-center justify-center">
                                <Icon
                                    :icon="CartIcon"
                                    class="size-5 fill-primary group-hover:fill-primary"
                                />
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="flex w-full items-center justify-center bg-[#F0F0F0] text-black transition-all duration-300 "
        >
            <div class="inner-container w-full py-2 md:py-5">
                <HeaderSwiper />
            </div>
        </div>
    </header>
</template>
